import { useMemo } from 'react';

import { SupportedC2cConnectorName } from '@portals/api/organizations';
import { C2C_INTEGRATIONS, C2CIntegrationItem } from '@portals/framework';
import { useIntegrations } from '@portals/redux';

type Integrations = ReturnType<typeof useIntegrations>;
type IntegrationsWithIds = ReturnType<typeof getActiveIntegrationsWithIds>;

export type AllowedC2cThirdPartyConnectors = Extract<
  SupportedC2cConnectorName,
  'zoom_v2' | 'mtr' | 'xio' | 'domotz'
>;

export interface OrgC2CIntegrationItem extends C2CIntegrationItem {
  href?: string;
  isComingSoon?: boolean;
  thirdPartyConnectors?: Array<AllowedC2cThirdPartyConnectors>;
  prioritized?: boolean;
  isLegacyIntegration?: boolean;
}

function getActiveIntegrationsWithIds(integrations: Integrations) {
  const integrationsWithIds = Object.entries(integrations).map(
    ([id, integration]) => ({
      id,
      ...integration,
    })
  );

  return integrationsWithIds.filter(
    (integration) => integration.state === 'active'
  );
}

function filterActiveIntegrationsList(
  integrationsList: OrgC2CIntegrationItem[],
  activeIntegrations: IntegrationsWithIds
) {
  return integrationsList.filter((integration) =>
    activeIntegrations.some(
      (activeIntegration) => activeIntegration.id === integration.id
    )
  );
}

export function useIntegrationsList() {
  return useMemo(() => {
    const integrationList: Array<OrgC2CIntegrationItem> = [
      {
        ...C2C_INTEGRATIONS.zoom,
        isLegacyIntegration: true,
      },
      {
        ...C2C_INTEGRATIONS.zoom_v2,
        prioritized: true,
      },
      {
        ...C2C_INTEGRATIONS.mtr,
        prioritized: true,
      },
      {
        ...C2C_INTEGRATIONS.webex,
        prioritized: true,
        isComingSoon: true,
      },
      C2C_INTEGRATIONS.xio,
      C2C_INTEGRATIONS.q_sys,
      C2C_INTEGRATIONS.bright_sign,
      C2C_INTEGRATIONS.domotz,
      C2C_INTEGRATIONS.biamp_workplace,
      C2C_INTEGRATIONS.neat,
      {
        ...C2C_INTEGRATIONS.appspace,
        isComingSoon: true,
      },

      {
        ...C2C_INTEGRATIONS.poly,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.sonos,
        thirdPartyConnectors: ['domotz'],
      },
      {
        ...C2C_INTEGRATIONS.viewsonic,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.yealink,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.absen,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.lg,
        thirdPartyConnectors: ['xio', 'zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.philips,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.christie,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.nec,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.samsung,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.elo,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.optoma,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.sharp,
        thirdPartyConnectors: ['xio', 'zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.epson,
        thirdPartyConnectors: ['xio', 'zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.panasonic,
        thirdPartyConnectors: ['xio', 'zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.vivitek,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.acer,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.audiocodes,
        thirdPartyConnectors: ['mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.aver,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.barco,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.clearone,
        thirdPartyConnectors: ['zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.dten,
        thirdPartyConnectors: ['zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.epos,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.hp,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.huddlecamhd,
        thirdPartyConnectors: ['zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.jabra,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.lenovo,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.logitech,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.maxhub,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.microsoft_surface,
        thirdPartyConnectors: ['mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.sennheiser,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.shure,
        thirdPartyConnectors: ['mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.smart,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.sony,
        thirdPartyConnectors: ['xio', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.vaddio,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.vdo360,
        thirdPartyConnectors: ['zoom_v2', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.yamaha,
        thirdPartyConnectors: ['zoom_v2', 'mtr', 'domotz'],
      },
      {
        ...C2C_INTEGRATIONS.novastar,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.yale,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS['2n'],
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.honeywell,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.lenel,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.assa,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.visionline,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.avigilon,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.brivo,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.controlbyweb,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS['4suites'],
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.pti,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.salto,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.pointgrab,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.cisco_meraki,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.hpe_aruba,
        isComingSoon: true,
      },
      {
        ...C2C_INTEGRATIONS.nureva,
        isComingSoon: true,
      },
    ];

    return integrationList;
  }, []);
}

export function useActiveCloudIntegrations() {
  const integrations = useIntegrations();
  const integrationsList = useIntegrationsList();

  const activeIntegrations = useMemo(() => {
    return getActiveIntegrationsWithIds(integrations);
  }, [integrations]);

  return useMemo(
    () => filterActiveIntegrationsList(integrationsList, activeIntegrations),
    [integrationsList, activeIntegrations]
  );
}
